import React, { useEffect, useId, useRef, useState } from 'react';
import NearViewport from './NearViewport';
import { VISITOR_MAPS } from '../../../content/landingHome';
import { EVENTS, track } from '../../../services/analytics';
import { STAT_TINT } from '../../StatBand';
import { US_STATE_NAMES } from '../../../utils/regions';

// Where people come from (#4607, mock D; #4742; density #4768). A US map and
// a world map on the green band. get-landing-stats sends each reached place's
// density STEP, never a count: 1 to 5 by the people there (site visitors and
// signed-in students, five log-scale steps computed on the server), and 0 for
// a place reached only by our videos (the lightest shade). The legend reads
// Fewer -> More with no number, and a hover or tap names the place and says
// "reached": one person in a small state must not be identifiable by a number.
//
// The outlines are static files (scripts/build_visitor_maps.mjs), fetched
// only when the band nears the screen, so the first paint (#4484) never
// waits on them. A failed fetch leaves the figures and the line, no map.

export const MAP_FILES = { us: '/maps/us-states.json', world: '/maps/world.json' };
export const REACHED = '#E3F0E2';
export const MAX_STEP = 5;
// The maps sit on a light card inside the green band (founder, 2026-10-18
// ~10 PM PT, #4777: "with green background everything camouflaging"), and
// each category reads on its own:
//   steps 1 to 5   a warm sequential ramp, pale yellow to deep red, falling in
//                  lightness at every step, so the order survives grayscale
//                  and deuteranopia (checked: every pair of categories at
//                  least 0.09 apart in luminance, and the stripes at least
//                  dE 70 from every fill under a simulated deuteranopia)
//   videos only    blue diagonal stripes, never another shade of the ramp
//   not yet        a light neutral grey with a visible outline: empty
export const PALETTE = {
    ramp: ['#FFD07A', '#FBA14A', '#EE6B2F', '#C93A26', '#8A1A16'],
    stripe: '#2F6DB5',
    notYet: '#EDECE8',
    notYetEdge: '#B9B5AC',
    card: '#FFFDF8',
    edge: '#FFFFFF',
    ink: '#24332A',
    muted: '#4E5B53',
};
export const HATCH = 'hatch';
// By step: 0 (our videos only) is the stripes, then the ramp.
export const DENSITY = [HATCH, ...PALETTE.ramp];
export const NOT_YET = PALETTE.notYet;
// A served step, or null (not reached). Anything else never draws.
export const stepOf = (places, code) => {
    const v = code && places ? places[code] : undefined;
    return Number.isInteger(v) && v >= 0 && v <= MAX_STEP ? v : null;
};
const REGION = (() => {
    try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
})();
export function placeName(scope, code) {
    if (scope === 'us') return US_STATE_NAMES[code] || code;
    try { return (REGION && REGION.of(code)) || code; } catch { return code; }
}

async function load(url) {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`${r.status}`);
    const d = await r.json();
    if (!d || typeof d.viewBox !== 'string' || !Array.isArray(d.shapes)) throw new Error('shape');
    return d;
}

/** The stripes for "seen our videos only", in a map's own units. */
function Stripes({ id, viewBox, color }) {
    const w = Number(String(viewBox).split(/\s+/)[2]) || 960;
    const size = Math.max(4, Math.round(w / 110));
    return (
        <pattern id={id} patternUnits="userSpaceOnUse" width={size} height={size} patternTransform="rotate(45)">
            <rect width={size} height={size} fill="#FFFFFF" />
            <rect width={size / 2} height={size} fill={color} />
        </pattern>
    );
}

/** A map. `shades` (by step) shades each place by its served step, HATCH
 * drawing the stripes; without it, every reached place takes `fill`.
 * `scope` names places for the hover title; `onPick(code)` reports a tap. */
export function MapSvg({ data, places, label, stroke, fill = REACHED, empty = NOT_YET, edge = PALETTE.edge,
    line = PALETTE.notYetEdge, shades = null, scope = null, onPick = null, stripe = PALETTE.stripe }) {
    const uid = `stripes-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
    const dots = data.dots && typeof data.dots === 'object' ? Object.entries(data.dots) : [];
    const color = (s) => {
        const c = shades ? shades[s] : fill;
        return c === HATCH ? `url(#${uid})` : c;
    };
    // A dot is too small for stripes (review of #4777): videos-only dots take
    // the stripe colour solid, still unlike every step of the ramp.
    const dotFill = (s) => (shades && shades[s] === HATCH ? stripe : color(s));
    const title = (code, s) => (scope && code
        ? <title>{`${placeName(scope, code)}: ${s === null ? VISITOR_MAPS.place.notYet : VISITOR_MAPS.place.reached}`}</title> : null);
    return (
        <svg viewBox={data.viewBox} role="img" aria-label={label} className="block h-auto w-full">
            {shades && shades.includes(HATCH) && <defs><Stripes id={uid} viewBox={data.viewBox} color={stripe} /></defs>}
            {data.shapes.map(([code, d], i) => {
                const s = stepOf(places, code);
                return <path key={`${code || 'x'}-${i}`} d={d} fill={s === null ? empty : color(s)} stroke={s === null ? line : edge}
                    strokeWidth={stroke} strokeLinejoin="round" data-place={code || undefined} data-reached={s === null ? '0' : '1'}
                    data-step={s === null ? undefined : s} onClick={onPick && code ? () => onPick(code) : undefined}>{title(code, s)}</path>;
            })}
            {dots.filter(([code]) => stepOf(places, code) !== null).map(([code, [x, y]]) => (
                <circle key={code} cx={x} cy={y} r={4} fill={dotFill(stepOf(places, code))} stroke={PALETTE.ink} strokeWidth={0.8}
                    data-place={code} data-reached="1" data-step={stepOf(places, code)}
                    onClick={onPick ? () => onPick(code) : undefined}>{title(code, stepOf(places, code))}</circle>
            ))}
        </svg>
    );
}

/** A legend swatch: a ramp colour, the stripes, or the empty grey. */
export function Swatch({ kind, color, className = 'h-4 w-4' }) {
    const style = kind === 'stripes'
        ? { background: `repeating-linear-gradient(45deg, ${PALETTE.stripe} 0 3px, #FFFFFF 3px 6px)`, borderColor: PALETTE.stripe }
        : kind === 'empty' ? { background: PALETTE.notYet, borderColor: PALETTE.notYetEdge } : { background: color, borderColor: 'transparent' };
    return <span className={`inline-block shrink-0 rounded-[4px] border ${className}`} style={style} />;
}

function PlayIcon() {
    return (
        <svg viewBox="0 0 12 12" aria-hidden="true" className="h-3 w-3 shrink-0" fill={PALETTE.stripe}>
            <path d="M3 1.8v8.4L10 6z" />
        </svg>
    );
}

/** The legend: the ramp as one bar from Fewer to More, then a chip each for
 * "seen our videos only" and "not yet". No numbers anywhere. */
export function Legend() {
    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm lg:justify-center" style={{ color: PALETTE.ink }}
            data-testid="landing-visitor-legend" aria-hidden="true">
            <span className="inline-flex items-center gap-2" data-testid="legend-ramp">
                <span>{VISITOR_MAPS.legend.fewer}</span>
                <span className="inline-flex overflow-hidden rounded-[4px]">
                    {PALETTE.ramp.map((c) => <span key={c} className="inline-block h-4 w-7" style={{ background: c }} />)}
                </span>
                <span>{VISITOR_MAPS.legend.more}</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: '#D9D5CC' }}
                data-testid="legend-videos">
                <Swatch kind="stripes" />
                <PlayIcon />
                {VISITOR_MAPS.legend.videosOnly}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: '#D9D5CC' }}
                data-testid="legend-not-yet">
                <Swatch kind="empty" />
                {VISITOR_MAPS.legend.notYet}
            </span>
        </div>
    );
}

function Maps({ reach }) {
    const hasWorld = Boolean(reach.world && reach.countries);
    const [maps, setMaps] = useState(null);
    const [active, setActive] = useState('us');
    const [picked, setPicked] = useState(null);
    const row = useRef(null);
    const viewed = useRef(false);
    const byTab = useRef(false);
    const box = useRef(null);

    useEffect(() => {
        let alive = true;
        Promise.all([load(MAP_FILES.us), hasWorld ? load(MAP_FILES.world) : Promise.resolve(null)])
            .then(([us, world]) => { if (alive) setMaps({ us, world }); })
            .catch(() => { if (alive) setMaps(false); });
        return () => { alive = false; };
    }, [hasWorld]);

    // Viewed once per page view, when the maps are on screen (or at once
    // where there is no IntersectionObserver).
    useEffect(() => {
        if (!maps || viewed.current || !box.current) return undefined;
        const fire = () => {
            if (viewed.current) return;
            viewed.current = true;
            track(EVENTS.LANDING_MAP_VIEWED, { maps: maps.world ? 'us_world' : 'us' });
        };
        if (typeof IntersectionObserver !== 'function') { fire(); return undefined; }
        const io = new IntersectionObserver((es) => {
            if (es.some((e) => e.isIntersecting)) { fire(); io.disconnect(); }
        }, { threshold: 0.3 });
        io.observe(box.current);
        return () => io.disconnect();
    }, [maps]);

    if (!maps) return maps === false ? null : <div style={{ minHeight: 240 }} />;

    const panels = [['us', maps.us, reach.us, VISITOR_MAPS.mapLabel.us({ states: reach.states }), 0.9]];
    if (maps.world) panels.push(['world', maps.world, reach.world, VISITOR_MAPS.mapLabel.world({ countries: reach.countries }), 0.4]);

    const show = (panel) => {
        const el = row.current;
        const i = panels.findIndex(([p]) => p === panel);
        // Tapping the panel already shown scrolls nothing, so it must not mark
        // the next swipe as a tab's (review of #4607).
        if (panel === active || i < 0) return;
        if (el) {
            byTab.current = true;
            const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            el.scrollTo?.({ left: i * el.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
        }
        setActive(panel);
        track(EVENTS.LANDING_MAP_PANEL_SWITCHED, { panel, via: 'tab' });
    };
    const onScroll = () => {
        const el = row.current;
        if (!el || !el.clientWidth) return;
        const panel = panels[Math.round(el.scrollLeft / el.clientWidth)]?.[0];
        if (!panel || panel === active) {
            if (panel === active) byTab.current = false;
            return;
        }
        setActive(panel);
        if (!byTab.current) track(EVENTS.LANDING_MAP_PANEL_SWITCHED, { panel, via: 'swipe' });
    };

    return (
        <div ref={box} data-testid="landing-visitor-maps">
            {panels.length > 1 && (
                <div className="mb-4 flex gap-2 lg:hidden" role="group" aria-label={VISITOR_MAPS.heading}>
                    {panels.map(([p]) => (
                        <button key={p} type="button" aria-pressed={active === p} onClick={() => show(p)}
                            className={`min-h-[44px] rounded-full border px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${active === p
                                ? 'border-[#E3F0E2] bg-[#E3F0E2] text-[#1A4D2E]' : 'border-white/40 bg-transparent text-white'}`}>
                            {VISITOR_MAPS.tabs[p]}
                        </button>
                    ))}
                </div>
            )}
            <div className="rounded-2xl p-3 shadow-[0_10px_30px_rgba(0,0,0,0.22)] sm:p-5" style={{ background: PALETTE.card }}
                data-testid="landing-visitor-card">
            <div ref={row} onScroll={onScroll} data-testid="landing-visitor-map-row"
                className="flex snap-x snap-mandatory gap-4 overflow-x-auto [scrollbar-width:none] lg:items-start lg:gap-10 lg:overflow-visible">
                {panels.map(([p, data, places, label, stroke]) => (
                    <figure key={p} className={`m-0 w-full min-w-0 shrink-0 snap-start lg:shrink ${p === 'us' ? 'lg:basis-[44%]' : 'lg:flex-1'}`}>
                        <figcaption className="mb-2 hidden text-sm font-semibold lg:block" style={{ color: PALETTE.ink }}>{VISITOR_MAPS.tabs[p]}</figcaption>
                        <MapSvg data={data} places={places || {}} label={label} stroke={stroke} shades={DENSITY} scope={p}
                            onPick={(code) => setPicked({ scope: p, code })} />
                    </figure>
                ))}
            </div>
            <p className="mt-2 min-h-[1.5rem] text-sm font-semibold lg:text-center" style={{ color: PALETTE.ink }}
                aria-live="polite" data-testid="landing-visitor-picked">
                {picked ? `${placeName(picked.scope, picked.code)}: ${stepOf(reach[picked.scope], picked.code) === null
                    ? VISITOR_MAPS.place.notYet : VISITOR_MAPS.place.reached}` : ''}
            </p>
            <Legend />
            <p className="mt-3 text-xs lg:text-center" style={{ color: PALETTE.muted }} data-testid="landing-visitor-fine-print">
                {VISITOR_MAPS.finePrint}
            </p>
            </div>
        </div>
    );
}

/** The reach block under the band's figures: heading, the line, the maps
 * (loaded near the screen), the legend and the fine print. Nothing without
 * a states figure and a US map from the server. */
export const WIDE = '(min-width: 1024px)';

export default function VisitorMaps({ reach }) {
    // Closed on a phone until tapped (the band sits above the hero there,
    // and the maps are about 380 px of it); open on a desktop. The prerender
    // and the first render agree (closed), then a desktop opens.
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (window.matchMedia?.(WIDE).matches) setOpen(true);
    }, []);
    if (!reach || !reach.states || !reach.us || typeof reach.us !== 'object') return null;
    const line = VISITOR_MAPS.line({ states: reach.states, countries: reach.world ? reach.countries : null });
    return (
        <div className="mt-8 border-t border-white/20 pt-6" data-landing-section="visitor_maps">
            <h3 className="font-serif text-xl font-bold text-white sm:text-2xl lg:text-center">{VISITOR_MAPS.heading}</h3>
            <p className={`mt-1 text-base ${STAT_TINT} lg:text-center`} data-testid="landing-visitor-line">{line}</p>
            <button type="button" aria-expanded={open} aria-controls="landing-visitor-maps-panel"
                onClick={() => setOpen((v) => !v)} data-testid="landing-visitor-map-toggle"
                className="mt-3 min-h-[44px] rounded-full border border-white/40 px-4 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white lg:hidden">
                {open ? VISITOR_MAPS.toggle.close : VISITOR_MAPS.toggle.open}
            </button>
            <div id="landing-visitor-maps-panel" hidden={!open}>
                {open && (
                    <NearViewport margin="400px" minHeight={240} className="mt-5">
                        <Maps reach={reach} />
                    </NearViewport>
                )}
            </div>
        </div>
    );
}
